<script setup>
import '@/assets/nangman_style/popup/saju-reading_mobile.scss'
import { DEV_MODE } from '@/config/dev-mode'
import { useStore } from 'vuex'
import { onMounted } from 'vue'
import { fetchBaseServiceAPI } from '@/api/baseServiceApi'
import fiveMixin from '@/composables/fiveMixin'
import LifeLuckChartCard from '@/components/calendar/drawer/pages/cards/LifeLuckChartCard.vue'
import DailyFortunePreviewCard from '@/components/calendar/drawer/pages/cards/DailyFortunePreviewCard.vue'
import DailyFortuneDrawerHeader from '@/components/calendar/drawer/DailyFortuneDrawerHeader.vue'

definePage({ meta: { layout: 'empty' } })

const props = defineProps({
  summaryParams: {
    type: Object,
  }
})
const emit = defineEmits(['close', 'redirectToLogin'])

const router = useRouter()

const store = useStore()
const userId = ref(null)
userId.value = store.getters['user/id']

/** 팝업 닫기 */
const close = () => {
  emit('close')
}

const initializing = ref(true);
const isDailyFortuneLoading = ref(false);
const isDailyFortuneLoaded = ref(false);

const lifeLuckChartData = ref({});
const parsedDailyFortune = ref({});
const dailyFortuneDetail = ref({ 일일운세: { 일운: {} } });
const selectedDayCellProps = ref({});

const { getHanjaTxt } = fiveMixin();

/** 낭만일진 요약 조회 */
const getDailyFortuneSummary = async (newParams) => {
  if (isDailyFortuneLoading.value) return;
  isDailyFortuneLoaded.value = false;
  isDailyFortuneLoading.value = true;

  try {
    const result = await fetchBaseServiceAPI({ ...newParams, analysisTypeId: 7 });

    const summaryData = result?.data;
    if (!summaryData || !summaryData.사주정보 || !summaryData.생활종합) return;

    processLifeLuckChart(summaryData.생활종합);
    processCriteriaInfo(summaryData.사주정보);
    dailyFortuneDetail.value.일일운세.일운.기준점수 = summaryData.요약해석;

    isDailyFortuneLoaded.value = true;
  } catch (e) {
    DEV_MODE && console.error('낭만일진 요약 조회 실패:', e);
  } finally {
    isDailyFortuneLoading.value = false;
    initializing.value = false;
  }
};

const processLifeLuckChart = (rawChart) => {
  lifeLuckChartData.value = Object.fromEntries(
    Object.entries(rawChart).map(([key, value]) => [key, value.달성확률])
  );
};

const processCriteriaInfo = (info) => {
  const energyValue = info['일운확률'];
  parsedDailyFortune.value = Object.fromEntries(
    Object.entries(info).map(([key, value]) => {
      if (key === '일운점수') return ['점수', getEnergyLevel(energyValue)];
      if (key === '일운확률') return ['확률', value];
      return [key, value];
    })
  );

  selectedDayCellProps.value.day = `${info.분석기간 || info.일운시작 || ''}`;
  selectedDayCellProps.value.handayju = info.일운갑자;
  selectedDayCellProps.value.dayju = formatDayju(info.일운갑자);
};

const formatDayju = (dayju) => {
  if (!dayju || dayju.length < 2) return '';
  return `${getHanjaTxt(dayju[0], 'sky')}${getHanjaTxt(dayju[1])}`;
};

const handleChartAction = ({action}) => {
  if (action === 'login') {
    emit('redirectToLogin', {redirectOption: 'fortune'})
  } else if (action === 'route') {
    router.push('/calendar/fortune')
  }
}

onMounted(async () => {
  await getDailyFortuneSummary(props.summaryParams);
})
</script>

<template>
  <div class="pop-saju-common-container">
    <div class="pop-header">
      <button @click="close"><i class="pop-header__close"></i></button>
      <div class="pop-header__text">
        <div class="pop-header__title">낭만일진</div>
        <div class="pop-header__subtitle">
          나를 이해하는 하루 오늘, 무료 낭만일진으로 시작해보세요!
        </div>
      </div>
    </div>
    <div class="pop-common-body">
      <div class="df-drawer__container popup df-drawer__summary">  
        <div
          class="df-drawer__body"
          :style="(!isDailyFortuneLoading && isDailyFortuneLoaded) ? 'position: relative' : ''"
        >
          <div
            v-if="initializing || isDailyFortuneLoading"
            class="empty-section"
          >
            <DailyFortuneDrawerIndicator />
          </div>
  
          <DailyFortuneDrawerError
            v-else-if="!initializing && !isDailyFortuneLoading && !isDailyFortuneLoaded"
            @confirm="closePopup"
          />
  
          <template v-else>
            <div class="drawer-section__wrapper">
              <div class="drawer-section">
                <div class="section-page">
                  <DailyFortuneDrawerHeader
                    :isDailyFortuneLoading="isDailyFortuneLoading"
                    :isDailyFortuneLoaded="isDailyFortuneLoaded"
                    :dailyCustomObject="parsedDailyFortune"
                    :selectedDayCellProps="selectedDayCellProps"
                    @closeDrawer="closePopup"
                  />
                  <DailyFortunePreviewCard
                    :dailyCustomObject="parsedDailyFortune"
                    :dailyFortuneDetail="dailyFortuneDetail"
                  />
                  <LifeLuckChartCard
                    :key="JSON.stringify(lifeLuckChartData)"
                    :isMobile="true"
                    :accessToken="userId"
                    :activeBlur="true"
                    :lifeLuckChartData="lifeLuckChartData"
                    @clickChartItem="() => {}"
                    @sendMessageToApp="handleChartAction"
                  />
                  <div style="height: 20px"></div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.pop-saju-common-container {
  width: 100%;
  height: 694px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  border-radius: 16px;
  background: var(--gray-100-white, #fff);
  .pop-header {
    width: 100%;
    background: var(--Orange-100, #fff8f5);
    height: 120px;
    border-radius: 16px 16px 0 0;
    text-align: center;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    .pop-header__close {
      position: absolute;
      right: 16px;
      top: 30px;
      width: 24px;
      height: 24px;
      background: url('@manseImages/x-icon.svg') center center no-repeat;
      background-size: contain;
    }
    .pop-header__text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      .pop-header__title {
        color: var(--Gray-800, #333);
        text-align: center;
        font-family: Pretendard;
        font-size: 24px;
        font-style: normal;
        font-weight: 700;
        line-height: 32px; /* 133.333% */
      }
      .pop-header__subtitle {
        color: var(--Gray-700, #575757);
        font-family: Pretendard;
        font-size: 15px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px; /* 160% */
      }
    }
  }
  .pop-common-body {
    padding: 20px 24px;
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>